/* eslint-disable @typescript-eslint/no-duplicate-type-constituents */
export type SocialPlatformMetrics =
  | FacebookMetrics
  | FacebookStoryMetrics
  | InstagramMetrics
  | InstagramStoryMetrics
  | TwitterMetrics
  | LinkedInMetrics
  | YouTubeMetrics
  | TikTokMetrics
  | ThreadsMetrics
  | GoogleBusinessProfileMetrics;

/**
 * Instagram has three distinct media-type-driven metric shapes (Image/Carousel,
 * Reel, Story). The variant is computed per active account from
 * `platformMediaType` + the post's first poster page; helpers downstream switch
 * grids/tooltips/icons on it.
 */
export type InstagramVariant = 'image' | 'reel' | 'story';

/**
 * Facebook splits into two grid shapes: Page-Post (impressions/reactions/etc)
 * and Page-Story (which uses Meta's STORY_* / PAGES_FB_STORY_* metric
 * vocabulary and adds an "Interactions" cell). Variant is selected from
 * `platformMediaType`.
 */
export type FacebookVariant = 'image' | 'story';

/**
 * Post-level metrics pipeline state. Mirrors PHP `MetricsJobState`. Read from
 * `SocialPost.metricsJobState`. Null for legacy posts written before PR 2 —
 * reducers must fall back to the old `post.metrics` emptiness derivation in
 * that case.
 */
export enum MetricsJobState {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  QUEUED = 'QUEUED',
  PARTIAL = 'PARTIAL',
  FETCHED = 'FETCHED',
  UNAVAILABLE = 'UNAVAILABLE',
  EXHAUSTED = 'EXHAUSTED',
}

/**
 * Per-account metrics health. Mirrors PHP `MetricsAvailability`. Stored on
 * each publishing-params object so the UI can show a per-account breakdown
 * when some accounts succeed and others don't.
 */
export enum MetricsAvailability {
  AVAILABLE = 'AVAILABLE',
  DELETED_ON_PLATFORM = 'DELETED_ON_PLATFORM',
  ACCESS_REVOKED = 'ACCESS_REVOKED',
  PERMISSIONS_PENDING = 'PERMISSIONS_PENDING',
  MAX_FAILURES_EXCEEDED = 'MAX_FAILURES_EXCEEDED',
  NOT_SUPPORTED = 'NOT_SUPPORTED',
  NOT_SUPPORTED_AFTER_WINDOW = 'NOT_SUPPORTED_AFTER_WINDOW',
}

/**
 * Typed result of a manual "refresh metrics" request. Returned by
 * `socialmedia/populateMetricsForPost/<id>` as part of
 * `ManualMetricsRefreshResponse`. Drives the refresh-button UX:
 * countdown, toasts, and disable-for-the-day.
 */
export enum ManualMetricsRefreshResult {
  REFRESHED = 'REFRESHED',
  FRESH_ENOUGH = 'FRESH_ENOUGH',
  COOLDOWN = 'COOLDOWN',
  DAILY_CAP_EXCEEDED = 'DAILY_CAP_EXCEEDED',
  UNAVAILABLE = 'UNAVAILABLE',
}

export interface BaseMetrics {
  numImpressions: number;
  numLikes: number;
  numClicks: number;
  numComments: number;
  numShares: number;
  numReach: number;
  numViews: number;
  // Optional cross-platform fields, present only when the platform reports them. See PHP `MetricInsights` for the canonical list.
  numSaves?: number;
  numProfileVisits?: number;
  numProfileActivity?: number;
  numFollowsAttributed?: number;
  numEngagements?: number;
  numOutboundClicks?: number;
  numNavigation?: number;
  avgWatchTimeSeconds?: number;
  totalWatchTimeSeconds?: number;
}

export type FacebookMetrics = BaseMetrics;

export type FacebookStoryMetrics = BaseMetrics;

export type InstagramMetrics = BaseMetrics;

export type InstagramStoryMetrics = BaseMetrics;

export interface TwitterMetrics extends BaseMetrics {
  numRetweets: number;
  numReplies: number;
  numQuoted: number;
  numBookmarked: number;
}

export interface LinkedInMetrics extends BaseMetrics {
  numEngagement: number;
  numUniqueImpressions: number;
}

export interface YouTubeMetrics extends BaseMetrics {
  numEstimatedMinutesWatched: number;
  numAverageViewDuration: number;
  numAverageViewPercentage: number;
}

export interface PinterestMetrics extends BaseMetrics {
  numReactions: number;
  numClickthrough: number;
}

export type TikTokMetrics = BaseMetrics;

/**
 * Threads insights expose `views`, `likes`, `replies` (→ numComments),
 * `reposts` (→ numShares), and `quotes`. Quotes are Threads-specific —
 * the count of quote-reposts of a thread — and live on this subclass.
 *
 * @see https://developers.facebook.com/docs/threads/insights
 */
export interface ThreadsMetrics extends BaseMetrics {
  numQuotes?: number;
}

/**
 * Google Business Profile metrics come from the location-level Performance
 * API (not per-post). Subclass-only fields surface GBP-specific actions
 * (calls, directions, conversations, bookings) that don't map to any other
 * platform. Maps vs. search impressions live on separate fields so the grid
 * can show them side-by-side.
 */
export interface GoogleBusinessProfileMetrics extends BaseMetrics {
  numMapsImpressions?: number;
  numCallClicks?: number;
  numDirectionRequests?: number;
  numConversations?: number;
  numBookings?: number;
}

/**
 * Universal prefix for each per-platform grid array. Kept at the 5 truly
 * universal keys so platform-specific arrays can spread it without duplicates.
 */
export const metricsBase: (keyof BaseMetrics)[] = ['numLikes', 'numComments', 'numReach', 'numImpressions', 'numShares'];

/**
 * Cells shown in the default multi-account "aggregate" view. Wider than
 * `metricsBase` because the aggregate sums across accounts and we want to
 * surface every sum-safe cross-platform signal here. Per-platform grids don't
 * use this — they compose their own arrays off `metricsBase`.
 */
export const metricsAggregate: (keyof BaseMetrics)[] = [
  ...metricsBase,
  'numViews',
  'numSaves',
  'numFollowsAttributed',
];

export const metricsInstagramPost: (keyof InstagramMetrics)[] = [...metricsBase, 'numSaves', 'numEngagements', 'numProfileVisits', 'numFollowsAttributed'];

/** Reel-specific cells; profile_visits/follows aren't exposed for reels by IG's Media Insights API. */
export const metricsInstagramVideo: (keyof InstagramMetrics)[] = [...metricsBase, 'numSaves', 'numEngagements', 'avgWatchTimeSeconds', 'totalWatchTimeSeconds'];

/** Story cells; saves/likes aren't exposed by IG's Story API. `replies` maps to numComments server-side, `navigation` is Story-only. */
export const metricsInstagramStory: (keyof InstagramStoryMetrics)[] = ['numViews', 'numReach', 'numComments', 'numShares', 'numProfileVisits', 'numFollowsAttributed', 'numNavigation'];
export const metricsTwitter: (keyof TwitterMetrics)[] = ['numLikes', 'numReplies', 'numRetweets', 'numQuoted', 'numBookmarked'];
export const metricsLinkedInPage: (keyof LinkedInMetrics)[] = [...metricsBase, 'numEngagement', 'numUniqueImpressions'];
export const metricsYouTube: (keyof YouTubeMetrics)[] = [
  'numLikes',
  'numComments',
  'numViews',
  'numShares',
  'numFollowsAttributed',
  'numAverageViewPercentage',
  'numAverageViewDuration',
  'numEstimatedMinutesWatched',
];
export const metricsPinterest: (keyof PinterestMetrics)[] = [...metricsBase, 'numReactions', 'numSaves', 'numOutboundClicks'];
export const metricsFacebook: (keyof FacebookMetrics)[] = [...metricsBase];

/** FB Story cells; FacebookStoryMetricsFacade maps Meta's `STORY_*` and `PAGES_FB_STORY_*` fields onto these keys. */
export const metricsFacebookStory: (keyof FacebookStoryMetrics)[] = ['numImpressions', 'numReach', 'numLikes', 'numComments', 'numShares', 'numEngagements'];

export const metricsTikTok: (keyof TikTokMetrics)[] = ['numLikes', 'numComments', 'numShares', 'numViews'];

/** Threads cells; replies map to numComments and reposts map to numShares server-side. numQuotes is Threads-only. */
export const metricsThreads: (keyof ThreadsMetrics)[] = ['numViews', 'numLikes', 'numComments', 'numShares', 'numQuotes'];
export const metricsGoogleBusinessProfile: (keyof GoogleBusinessProfileMetrics)[] = [
  'numImpressions',
  'numMapsImpressions',
  'numOutboundClicks',
  'numCallClicks',
  'numDirectionRequests',
  'numConversations',
  'numBookings',
];

export type AllMetricKeys =
  | undefined
  | keyof BaseMetrics
  | keyof InstagramMetrics
  | keyof InstagramStoryMetrics
  | keyof TwitterMetrics
  | keyof LinkedInMetrics
  | keyof YouTubeMetrics
  | keyof PinterestMetrics
  | keyof FacebookMetrics
  | keyof FacebookStoryMetrics
  | keyof TikTokMetrics
  | keyof ThreadsMetrics
  | keyof GoogleBusinessProfileMetrics;

export interface MetricLabels {
  numImpressions: string;
  numLikes: string;
  numClicks: string;
  numComments: string;
  numShares: string;
  numReach: string;
  numViews: string;
  numSaves: string;
  numProfileVisits: string;
  numFollowsAttributed: string;
  numEngagements: string;
  numOutboundClicks: string;
  numNavigation: string;
  avgWatchTimeSeconds: string;
  totalWatchTimeSeconds: string;
  numRetweets: string;
  numReplies: string;
  numQuoted: string;
  numBookmarked: string;
  numEngagement: string;
  numUniqueImpressions: string;
  numAverageViewPercentage: string;
  numAverageViewDuration: string;
  numEstimatedMinutesWatched: string;
  numReactions: string;
  numMapsImpressions: string;
  numCallClicks: string;
  numDirectionRequests: string;
  numConversations: string;
  numBookings: string;
  numQuotes: string;
}

export const getReadableMetricByKey = (key: keyof MetricLabels, isFacebook: boolean): string => {
  const labels: MetricLabels = {
    numImpressions: window.i18next.t('pmwjs_impressions'),
    numLikes: isFacebook ? window.i18next.t('pmwjs_reactions') : window.i18next.t('pmwjs_likes'),
    numClicks: window.i18next.t('pmwjs_clicks'),
    numComments: window.i18next.t('pmwjs_comments'),
    numShares: window.i18next.t('pmwjs_shares'),
    numReach: window.i18next.t('pmwjs_reach'),
    numViews: window.i18next.t('pmwjs_views'),
    numSaves: window.i18next.t('pmwjs_saves'),
    numProfileVisits: window.i18next.t('pmwjs_profile_visits'),
    numFollowsAttributed: window.i18next.t('pmwjs_follows'),
    numEngagements: window.i18next.t('pmwjs_engagements'),
    numOutboundClicks: window.i18next.t('pmwjs_outbound_clicks'),
    numNavigation: window.i18next.t('pmwjs_navigation'),
    avgWatchTimeSeconds: window.i18next.t('pmwjs_avg_watch_time'),
    totalWatchTimeSeconds: window.i18next.t('pmwjs_total_watch_time'),
    numRetweets: window.i18next.t('pmwjs_retweets'),
    numReplies: window.i18next.t('pmwjs_replies'),
    numQuoted: window.i18next.t('pmwjs_quotes'),
    numBookmarked: window.i18next.t('pmwjs_bookmarks'),
    numEngagement: window.i18next.t('pmwjs_engagement_rate'),
    numUniqueImpressions: window.i18next.t('pmwjs_unique_impressions'),
    numAverageViewPercentage: window.i18next.t('pmwjs_avg_view_percentage'),
    numAverageViewDuration: window.i18next.t('pmwjs_avg_view_duration'),
    numEstimatedMinutesWatched: window.i18next.t('pmwjs_total_watch_time'),
    numReactions: window.i18next.t('pmwjs_reaction'),
    numMapsImpressions: window.i18next.t('pmwjs_maps_impressions'),
    numCallClicks: window.i18next.t('pmwjs_call_clicks'),
    numDirectionRequests: window.i18next.t('pmwjs_direction_requests'),
    numConversations: window.i18next.t('pmwjs_conversations'),
    numBookings: window.i18next.t('pmwjs_bookings'),
    numQuotes: window.i18next.t('pmwjs_quotes'),
  };

  return labels[key];
};

export const getMetricIconByKey = (key: keyof MetricLabels): string => {
  const labels: MetricLabels = {
    numImpressions: `icon-teamwork`,
    numLikes: `icon-thumbs-up`,
    numClicks: `icon-thumbs-up`,
    numComments: `icon-message`,
    numShares: `icon-share-external`,
    numReach: `icon-megaphone`,
    numViews: `icon-eye`,
    numSaves: `icon-bookmark`,
    numProfileVisits: `icon-user`,
    numFollowsAttributed: `icon-group-add`,
    numEngagements: `icon-causes`,
    numOutboundClicks: `icon-external-link`,
    numNavigation: `icon-mouse-click`,
    avgWatchTimeSeconds: `icon-timer`,
    totalWatchTimeSeconds: `icon-clock-o`,
    numRetweets: `icon-retweet`,
    numReplies: `icon-message`,
    numQuoted: `icon-message`,
    numBookmarked: `icon-bookmark`,
    numEngagement: `icon-causes`,
    numUniqueImpressions: `icon-bullseye`,
    numAverageViewPercentage: `icon-pie-chart`,
    numAverageViewDuration: `icon-timer`,
    numEstimatedMinutesWatched: `icon-clock-o`,
    numReactions: `icon-thumbs-up`,
    numMapsImpressions: `icon-map`,
    numCallClicks: `icon-phone`,
    numDirectionRequests: `icon-location`,
    numConversations: `icon-chat`,
    numBookings: `icon-calendar`,
    numQuotes: `icon-quote`,
  };

  return labels[key];
};

const getTooltipTextForLikes = (isDefaultSelection: boolean, isFacebook: boolean): string => {
  if (isDefaultSelection) {
    return window.i18next.t('pmwjs_likes_tooltip_text_default_level');
  }

  if (isFacebook) {
    return window.i18next.t('pmwjs_reactions_tooltip_text');
  }

  return window.i18next.t('pmwjs_likes_tooltip_text');
};

export const getMetricIconTooltipByKey = (key: keyof MetricLabels, isDefaultSelection: boolean, isFacebook: boolean): string => {
  const labels: MetricLabels = {
    numImpressions: isDefaultSelection ? window.i18next.t('pmwjs_impressions_tooltip_text_default_level') : window.i18next.t('pmwjs_impressions_tooltip_text'),
    numLikes: getTooltipTextForLikes(isDefaultSelection, isFacebook),
    numClicks: window.i18next.t('pmwjs_clicks_tooltip_text'),
    numComments: isDefaultSelection ? window.i18next.t('pmwjs_comments_tooltip_text_default_level') : window.i18next.t('pmwjs_comments_tooltip_text'),
    numShares: isDefaultSelection ? window.i18next.t('pmwjs_shares_tooltip_text_default_level') : window.i18next.t('pmwjs_shares_tooltip_text'),
    numReach: isDefaultSelection ? window.i18next.t('pmwjs_reach_tooltip_text_default_level') : window.i18next.t('pmwjs_reach_tooltip_text'),
    numViews: window.i18next.t('pmwjs_views_tooltip_text'),
    numSaves: window.i18next.t('pmwjs_saves_tooltip_text'),
    numProfileVisits: window.i18next.t('pmwjs_profile_visits_tooltip_text'),
    numFollowsAttributed: window.i18next.t('pmwjs_follows_tooltip_text'),
    numEngagements: window.i18next.t('pmwjs_engagements_tooltip_text'),
    numOutboundClicks: window.i18next.t('pmwjs_outbound_clicks_tooltip_text'),
    numNavigation: window.i18next.t('pmwjs_metrics_tooltip_num_navigation'),
    avgWatchTimeSeconds: window.i18next.t('pmwjs_avg_watch_time_tooltip_text'),
    totalWatchTimeSeconds: window.i18next.t('pmwjs_total_watch_time_tooltip_text'),
    numRetweets: window.i18next.t('pmwjs_retweets_tooltip_text'),
    numReplies: window.i18next.t('pmwjs_replies_tooltip_text'),
    numQuoted: window.i18next.t('pmwjs_quotes_tooltip_text'),
    numBookmarked: window.i18next.t('pmwjs_bookmarks_tooltip_text'),
    numEngagement: window.i18next.t('pmwjs_engagement_tooltip_text'),
    numUniqueImpressions: window.i18next.t('pmwjs_unique_impressions_tooltip_text'),
    numAverageViewPercentage: window.i18next.t('pmwjs_avg_view_percentage_tooltip_text'),
    numAverageViewDuration: window.i18next.t('pmwjs_avg_view_duration_tooltip_text'),
    numEstimatedMinutesWatched: window.i18next.t('pmwjs_total_watch_time_tooltip_text'),
    numReactions: window.i18next.t('pmwjs_reactions_tooltip_text'),
    numMapsImpressions: window.i18next.t('pmwjs_maps_impressions_tooltip_text'),
    numCallClicks: window.i18next.t('pmwjs_call_clicks_tooltip_text'),
    numDirectionRequests: window.i18next.t('pmwjs_direction_requests_tooltip_text'),
    numConversations: window.i18next.t('pmwjs_conversations_tooltip_text'),
    numBookings: window.i18next.t('pmwjs_bookings_tooltip_text'),
    numQuotes: window.i18next.t('pmwjs_metrics_tooltip_num_quotes'),
  };

  return labels[key];
};

export const getYouTubeTooltipByKey = (key: keyof MetricLabels): string => {
  const labels: MetricLabels = {
    numBookmarked: '',
    numEngagement: '',
    numEngagements: '',
    numOutboundClicks: '',
    numNavigation: '',
    avgWatchTimeSeconds: '',
    totalWatchTimeSeconds: '',
    numQuoted: '',
    numReactions: '',
    numReplies: '',
    numRetweets: '',
    numProfileVisits: '',
    numFollowsAttributed: window.i18next.t('pmwjs_follows_tooltip_text'),
    numUniqueImpressions: '',
    numImpressions: window.i18next.t('pmwjs_video_impressions_tooltip_text'),
    numLikes: window.i18next.t('pmwjs_video_likes_tooltip_text'),
    numClicks: window.i18next.t('pmwjs_video_clicks_tooltip_text'),
    numViews: window.i18next.t('pmwjs_video_views_tooltip_text'),
    numComments: window.i18next.t('pmwjs_video_comments_tooltip_text'),
    numShares: window.i18next.t('pmwjs_video_shares_tooltip_text'),
    numReach: window.i18next.t('pmwjs_video_reach_tooltip_text'),
    numSaves: window.i18next.t('pmwjs_video_saves_tooltip_text'),
    numAverageViewPercentage: window.i18next.t('pmwjs_avg_view_percentage_tooltip_text'),
    numAverageViewDuration: window.i18next.t('pmwjs_avg_view_duration_tooltip_text'),
    numEstimatedMinutesWatched: window.i18next.t('pmwjs_total_watch_time_tooltip_text'),
    numMapsImpressions: '',
    numCallClicks: '',
    numDirectionRequests: '',
    numConversations: '',
    numBookings: '',
    numQuotes: '',
  };

  return labels[key];
};
